import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ICON } from '@core/util/icon';
import {
	AssociatedContactType,
	Associations,
	BusinessClient,
	ClientType,
	PersonClient,
} from '@models/clients';
import { ContactInfo } from '@models/lender';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { ClientCreationService } from '@services/closer-api/client-creation/client-creation.service';
import { ClientsState } from '@state/clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable, combineLatest } from 'rxjs';
import { GlobalProfileComponent } from 'src/app/private/global-profile/global-profile.component';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

export enum LenderClientKebebOptionTypes {
	VIEW_GLOBAL_PROFILE = 'VIEW_GLOBAL_PROFILE',
	REMOVE = 'REMOVE',
	SET_PRIMARY = 'SET_PRIMARY',
}

export enum LenderClientAssociationTypes {
	INVESTOR = 'INVESTOR',
	ASSOCIATED_CONTACT = 'ASSOCIATED_CONTACT',
}

export interface LenderClientAssociation {
	associationType: LenderClientAssociationTypes;
	baseClientId: string;
	associatingClientId: string;
}

@UntilDestroy()
@Component({
	selector: 'app-client-lender-card',
	templateUrl: './client-lender-card.component.html',
	standalone: false,
})
export class ClientLenderCardComponent implements OnInit {
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Input() primary: boolean = false;
	@Input() client: PersonClient | BusinessClient;
	@Input() parentClient: PersonClient | BusinessClient;
	@Input() cardType: 'lender' | 'associated' = 'lender';
	@Input() collapsable: boolean = false;
	@Input() collapsed: boolean = false;
	@Output() collapsedChange = new EventEmitter<boolean>();
	@Input() highlightOnExpand: boolean = false;
	@Input() hideKebab: boolean = false;

	@Output() onRemoveLenderClient = new EventEmitter<void>();
	@Output() onAddAssociatedClient = new EventEmitter<LenderClientAssociation>();
	@Output() onSetAsPrimary = new EventEmitter<string>();

	@Select(ClientsState.getClientAssociations)
	clientAssociationsFilter$: Observable<(filter: string) => Associations>;
	@Select(ClientsState.getAllClients) clients$: Observable<
		(PersonClient | BusinessClient)[]
	>;
	public readonly clientTypes = ClientType;
	public readonly associationTypes = LenderClientAssociationTypes;
	public showClientSearch = false;
	public clientAssociations: Associations;
	public associatedClients: { [key: string]: PersonClient | BusinessClient } =
		{};
	public contactDetails: ContactInfo;
	public ICON = ICON;
	public ClientKebabOptions: DropdownOption<LenderClientKebebOptionTypes>[] = [
		{
			name: 'View global profile',
			value: LenderClientKebebOptionTypes.VIEW_GLOBAL_PROFILE,
			icon: ICON.EDIT,
		},
		{
			name: 'Set as primary',
			value: LenderClientKebebOptionTypes.SET_PRIMARY,
			icon: ICON.STAR,
		},
		{
			name: 'Remove',
			value: LenderClientKebebOptionTypes.REMOVE,
			classes: ['text-red'],
			icon: ICON.DELETE,
		},
	];

	public AssociationClientKebabOptions: DropdownOption<LenderClientKebebOptionTypes>[] =
		this.cardType === 'lender'
			? this.ClientKebabOptions
			: this.ClientKebabOptions.filter(
					(option) => option.value !== LenderClientKebebOptionTypes.SET_PRIMARY,
				);

	constructor(
		private drawerService: DrawerService,
		private clientCreationService: ClientCreationService,
	) {}

	ngOnInit() {
		combineLatest([this.clientAssociationsFilter$, this.clients$])
			.pipe(untilDestroyed(this))
			.subscribe(([filter, clients]) => {
				this.associatedClients = {};
				this.clientAssociations = filter(this.client?.id);
				Object.keys(this.clientAssociations ?? {}).forEach((clientId) => {
					this.associatedClients[clientId] = clients?.find(
						(client) => client.id === clientId,
					);
				});
			});
	}

	onKebabSelect(
		clientId: string,
		$event: DropdownResult<DropdownOption<LenderClientKebebOptionTypes>>,
	) {
		if ($event.result.value === LenderClientKebebOptionTypes.REMOVE) {
			this.onRemoveLenderClient.emit();
		} else if (
			$event.result.value === LenderClientKebebOptionTypes.VIEW_GLOBAL_PROFILE
		) {
			this.openClientDrawer(clientId);
		} else if (
			$event.result.value === LenderClientKebebOptionTypes.SET_PRIMARY
		) {
			this.onSetAsPrimary.emit(clientId);
		}
	}

	onAssociationKebabSelect(
		clientId: string,
		$event: DropdownResult<DropdownOption<LenderClientKebebOptionTypes>>,
	) {
		if ($event.result.value === LenderClientKebebOptionTypes.REMOVE) {
			this.clientCreationService
				.RemoveAssociation({
					associatedContactType: AssociatedContactType.SIGNING_OFFICER,
					parentClientId: this.parentClient.id,
					childClientId: clientId,
				})
				.toPromise();
		} else if (
			$event.result.value === LenderClientKebebOptionTypes.VIEW_GLOBAL_PROFILE
		) {
			this.goToGlobalProfile(clientId);
		}
	}

	onAddAssociatedContact() {
		this.showClientSearch = true;
	}

	toggleShowClientSearch() {
		this.showClientSearch = !this.showClientSearch;
	}

	onClientSelected(
		clientId: string,
		associationType: LenderClientAssociationTypes,
	) {
		this.onAddAssociatedClient.emit({
			baseClientId: this.client.id,
			associationType,
			associatingClientId: clientId,
		});
	}

	goToGlobalProfile(clientId: string) {
		this.openClientDrawer(clientId);
	}

	openClientDrawer(clientId: string) {
		this.drawerService.openDrawer(GlobalProfileComponent, {
			title: 'Global Profile',
			data: { clientId: clientId },
		});
	}

	toggleCollapsed() {
		if (this.collapsable) {
			this.collapsed = !this.collapsed;
			this.collapsedChange.emit(this.collapsed);
		}
	}
}
